import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import './denomination-count-grid';
import { ApiFactory } from '../api/easypos-api';
import {
  IShopDay,
  IShift,
  ShopDayState,
  IDenominationCount,
} from '../api/model/requests';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { styles as sharedStyles } from '../styles/shared-styles';
import { resolveRouterPath, router } from '../router';
import { getOrCreateDeviceId } from '../datatypes/device';
import { currencyDenominations } from '../datatypes/currency';
import type { DenominationCountGrid } from './denomination-count-grid';

const Base = WithUserProfileConsumer(LitElement);

// Phase J2 — the operator's day + shift control. Replaces the Phase-D manual
// Open/Close Day buttons: the trading day is now SHIFT-driven (first shift opens
// it, last shift end closes it). This bar shows the day status and THIS device's
// shift, with the one button that matters: Start shift (when off) or End shift
// (when on). Cash-on shops capture the opening float / counted close; cash-off
// shops start/end with no money entry.
//
// Emits `day-changed` (bubbles + composed) with the device's shift presence so
// host pages gate the till on "is THIS device on a shift?" (the server enforces
// the same gate — a sale without an open shift is rejected).
@customElement('shop-day-bar')
export class ShopDayBar extends Base {
  @property({ type: String }) shopName = '';

  @state() private _state: ShopDayState | 'unknown' = 'unknown';
  @state() private _day: IShopDay | null = null;
  @state() private _shift: IShift | null = null;
  @state() private _deviceId: string | null = null;
  @state() private _busy = false;
  @state() private _error: string | null = null;
  @state() private _notice: string | null = null;

  @state() private _startDialogOpen = false;
  @state() private _openingFloat = '';
  // J3 — the optional note the /cash route has always taken and this one dropped.
  // The two routes reach the SAME endShift/startShift, so an operator explaining a
  // float top-up or a short count had their explanation kept or lost purely by
  // which button they happened to press.
  @state() private _openingNote = '';
  @state() private _endDialogOpen = false;
  @state() private _countedClose = '';
  @state() private _closeNote = '';
  @state() private _handoverDialogOpen = false;
  @state() private _handoverPin = '';

  @query('#start-grid') private _startGrid?: DenominationCountGrid;
  @query('#end-grid') private _endGrid?: DenominationCountGrid;

  // J3 — count the cash by denomination when the active currency has a known
  // denomination set. Unknown currencies (empty set) fall back to a single
  // total field, so the surface degrades honestly rather than offering a grid
  // it can't populate.
  private get _useGrid(): boolean {
    return currencyDenominations().length > 0;
  }

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }
      .bar {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex-wrap: wrap;
        padding: 0.5rem 0.75rem;
        border: 1px solid var(--sl-color-neutral-200);
        border-radius: var(--sl-border-radius-medium);
        background: var(--sl-color-neutral-0);
      }
      .label {
        font-weight: 600;
      }
      .spacer {
        flex: 1 1 auto;
      }
      .error {
        flex-basis: 100%;
        color: var(--sl-color-danger-600);
        font-size: 0.875rem;
      }
      .notice {
        flex-basis: 100%;
        color: var(--sl-color-neutral-600);
        font-size: 0.875rem;
      }
    `,
  ];

  private get _cashOn(): boolean {
    const shop = this.userProfile?.shopAccess?.find(
      (s) => s.shopName.toLowerCase() === this.shopName.toLowerCase()
    );
    return shop?.subscription?.allowsCashManagement === true;
  }

  // Register lock is only offered where a staff PIN can reopen it: staff PINs
  // are a Silver+ feature (server-side StaffPinResolver refuses below Silver),
  // so on Free/Bronze the Lock button would clear the token and strand the
  // operator on the password fallback. The token's shop-access snapshot is the
  // only tier signal the client has; it cannot see whether THIS user personally
  // has a PIN set, so the gate is per-shop tier, not per-user.
  private get _lockAvailable(): boolean {
    const shop = this.userProfile?.shopAccess?.find(
      (s) => s.shopName.toLowerCase() === this.shopName.toLowerCase()
    );
    const tier = shop?.subscription?.tier;
    return tier === 'silver' || tier === 'gold';
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.refresh();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('shopName')) void this.refresh();
  }

  // Re-read the current day + this device's shift. Public so host pages can
  // refresh after their own actions (e.g. clearing the board).
  async refresh(): Promise<void> {
    if (!this.shopName) return;
    const api = ApiFactory.authenticated();
    this._deviceId ??= await getOrCreateDeviceId();

    const [dayR, shiftR] = await Promise.all([
      api.getCurrentDay(this.shopName),
      api.getCurrentShift(this.shopName, this._deviceId),
    ]);

    if (!dayR.success || !dayR.data) {
      this._state = 'unknown';
    } else if ('id' in dayR.data) {
      this._day = dayR.data;
      this._state = dayR.data.state;
    } else {
      this._day = null;
      this._state = 'closed';
    }

    this._shift =
      shiftR.success && shiftR.data && 'id' in shiftR.data ? shiftR.data : null;
    this._emit();
  }

  private _emit(): void {
    this.dispatchEvent(
      new CustomEvent('day-changed', {
        detail: {
          state: this._state,
          day: this._day,
          hasOpenShift: !!this._shift,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private async _openStart(): Promise<void> {
    this._error = null;
    this._notice = null;
    if (!this._cashOn) {
      void this._submitStart();
      return;
    }
    this._openingFloat = '';
    this._openingNote = '';
    this._startDialogOpen = true;
    // Clear any counts left from a previous open once the grid has rendered.
    await this.updateComplete;
    this._startGrid?.reset();
  }

  private async _submitStart(): Promise<void> {
    if (this._busy || !this._deviceId) return;
    let float: number | null = null;
    let counts: IDenominationCount[] | null = null;
    if (this._cashOn) {
      if (this._useGrid) {
        // The grid is the source of truth: derive the float from the counts.
        counts = this._startGrid?.counts ?? [];
        float = this._startGrid?.total ?? 0;
      } else {
        float = parseFloat(this._openingFloat);
        if (Number.isNaN(float) || float < 0) {
          this._error = 'Opening float must be a non-negative number.';
          return;
        }
      }
    }
    this._busy = true;
    this._error = null;
    const r = await ApiFactory.authenticated().startShift(this.shopName, {
      deviceId: this._deviceId,
      openingFloat: float,
      openingCounts: counts,
      openingNote: this._openingNote || null,
    });
    this._busy = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Could not start your shift.';
      return;
    }
    this._startDialogOpen = false;
    await this.refresh();
  }

  private async _openEnd(): Promise<void> {
    this._error = null;
    this._notice = null;
    if (!this._cashOn) {
      void this._submitEnd();
      return;
    }
    this._countedClose = '';
    this._closeNote = '';
    this._endDialogOpen = true;
    await this.updateComplete;
    this._endGrid?.reset();
  }

  private async _submitEnd(): Promise<void> {
    if (this._busy || !this._shift) return;
    let counted: number | null = null;
    let counts: IDenominationCount[] | null = null;
    if (this._cashOn) {
      if (this._useGrid) {
        counts = this._endGrid?.counts ?? [];
        counted = this._endGrid?.total ?? 0;
      } else {
        counted = parseFloat(this._countedClose);
        if (Number.isNaN(counted) || counted < 0) {
          this._error = 'Counted close must be a non-negative number.';
          return;
        }
      }
    }
    this._busy = true;
    this._error = null;
    const r = await ApiFactory.authenticated().endShift(this.shopName, {
      shiftId: this._shift.id,
      countedClose: counted,
      closingCounts: counts,
      closeNote: this._closeNote || null,
    });
    this._busy = false;
    if (!r.success || !r.data) {
      // The most common refusal is the "clear the board before ending the last
      // shift" gate — surface it verbatim.
      this._error = r.error ?? 'Could not end your shift.';
      return;
    }
    this._endDialogOpen = false;
    await this.refresh();
    if (!this._shift && this._state === 'closed') {
      this._notice = 'Shift ended. The trading day is now closed.';
    }
  }

  // Manual register lock. Fires a window event that app-index turns into the
  // full-viewport lock screen (it owns identity + the lock state); the day-bar
  // just requests it. Unlock is a staff PIN on the lock screen.
  private _lockRegister(): void {
    window.dispatchEvent(new CustomEvent('lock-register'));
  }

  private _openHandover(): void {
    this._error = null;
    this._notice = null;
    this._handoverPin = '';
    this._handoverDialogOpen = true;
  }

  private async _submitHandover(): Promise<void> {
    if (this._busy || !this._shift) return;
    const pin = this._handoverPin.trim();
    if (!/^\d{4,6}$/.test(pin)) {
      this._error = 'PIN must be 4-6 digits.';
      return;
    }
    this._busy = true;
    this._error = null;
    const r = await ApiFactory.authenticated().handOverShift(this.shopName, {
      shiftId: this._shift.id,
      pin,
    });
    this._busy = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Could not hand over the shift.';
      return;
    }
    this._handoverDialogOpen = false;
    this._handoverPin = '';
    await this.refresh();
    this._notice =
      'Shift handed over. The drawer balance carried with no recount.';
  }

  private _viewReport(dayId: string): void {
    router.navigate(resolveRouterPath(`shop/days/${dayId}/z-report`));
  }

  private _viewDays(): void {
    router.navigate(resolveRouterPath('shop/days'));
  }

  render() {
    if (this._state === 'unknown') return nothing;

    const onShift = !!this._shift;
    const closed = this._state === 'closed';
    const closing = this._state === 'closing';

    return html`
      <div class="bar">
        ${
          closed
            ? html`<sl-badge variant="neutral" pill>Day closed</sl-badge>
                <span class="label">No trading day open</span>`
            : nothing
        }
        ${
          this._state === 'open'
            ? html`<sl-badge variant="success" pill>Day open</sl-badge>
                <span class="label"
                  >${this._day?.displayLabel ?? 'Trading day'}</span
                >`
            : nothing
        }
        ${
          closing
            ? html`<sl-badge variant="warning" pill>Day closing</sl-badge>
                <span class="label">Finishing up…</span>`
            : nothing
        }
        ${
          !closed
            ? html`<sl-badge variant=${onShift ? 'primary' : 'neutral'} pill>
                ${onShift ? 'On shift' : 'Off shift'}
              </sl-badge>`
            : nothing
        }

        <span class="spacer"></span>

        ${
          onShift
            ? html`<sl-button
                  size="small"
                  ?loading=${this._busy}
                  @click=${this._openHandover}
                >
                  <sl-icon slot="prefix" name="arrow-left-right"></sl-icon>
                  Hand over
                </sl-button>
                <sl-button
                  size="small"
                  variant="warning"
                  ?loading=${this._busy}
                  @click=${this._openEnd}
                >
                  <sl-icon slot="prefix" name="box-arrow-right"></sl-icon>
                  End shift
                </sl-button>`
            : closing
              ? nothing
              : html`<sl-button
                  size="small"
                  variant="primary"
                  ?loading=${this._busy}
                  @click=${this._openStart}
                >
                  <sl-icon slot="prefix" name="box-arrow-in-right"></sl-icon>
                  Start shift
                </sl-button>`
        }
        ${
          closed && this._day
            ? html`<sl-button
                variant="primary"
                size="small"
                @click=${() => {
                  if (this._day) this._viewReport(this._day.id);
                }}
              >
                <sl-icon slot="prefix" name="calendar-check"></sl-icon>
                View Z-report
              </sl-button>`
            : nothing
        }
        ${
          this._lockAvailable
            ? html`<sl-button
                size="small"
                variant="text"
                @click=${this._lockRegister}
                title="Lock this register (staff PIN to unlock)"
              >
                <sl-icon slot="prefix" name="lock"></sl-icon>
                Lock
              </sl-button>`
            : nothing
        }

        <sl-button size="small" variant="text" @click=${this._viewDays}>
          <sl-icon slot="prefix" name="calendar-check"></sl-icon>
          Z-reports
        </sl-button>

        ${this._error ? html`<div class="error">${this._error}</div>` : nothing}
        ${this._notice ? html`<div class="notice">${this._notice}</div>` : nothing}
      </div>

      <sl-dialog
        class="full-page-on-phone"
        label="Start shift"
        ?open=${this._startDialogOpen}
        @sl-request-close=${() => (this._startDialogOpen = false)}
      >
        ${
          this._useGrid
            ? html`<p class="notice">
                  Count the cash in the till by denomination.
                </p>
                <denomination-count-grid
                  id="start-grid"
                  .denominations=${currencyDenominations()}
                ></denomination-count-grid>`
            : html`<sl-input
                label="Opening float"
                help-text="Cash currently in the till."
                type="number"
                inputmode="decimal"
                step="0.01"
                min="0"
                .value=${this._openingFloat}
                @sl-input=${(e: Event) => (this._openingFloat = (e.target as HTMLInputElement).value)}
                autofocus
                required
              ></sl-input>`
        }
        <sl-textarea
          label="Note (optional)"
          rows="2"
          .value=${this._openingNote}
          @sl-input=${(e: Event) => (this._openingNote = (e.target as HTMLTextAreaElement).value)}
        ></sl-textarea>
        <sl-button slot="footer" @click=${() => (this._startDialogOpen = false)}
          >Cancel</sl-button
        >
        <sl-button
          slot="footer"
          variant="primary"
          ?loading=${this._busy}
          ?disabled=${this._busy}
          @click=${this._submitStart}
          >Start shift</sl-button
        >
      </sl-dialog>

      <sl-dialog
        class="full-page-on-phone"
        label="End shift"
        ?open=${this._endDialogOpen}
        @sl-request-close=${() => (this._endDialogOpen = false)}
      >
        ${
          this._useGrid
            ? html`<p class="notice">
                  Count the drawer by denomination. Variance is computed against
                  expected.
                </p>
                <denomination-count-grid
                  id="end-grid"
                  .denominations=${currencyDenominations()}
                ></denomination-count-grid>`
            : html`<sl-input
                label="Counted close"
                help-text="Count the drawer and enter the total. Variance is computed against expected."
                type="number"
                inputmode="decimal"
                step="0.01"
                min="0"
                .value=${this._countedClose}
                @sl-input=${(e: Event) => (this._countedClose = (e.target as HTMLInputElement).value)}
                autofocus
                required
              ></sl-input>`
        }
        <sl-textarea
          label="Note (optional)"
          rows="2"
          .value=${this._closeNote}
          @sl-input=${(e: Event) => (this._closeNote = (e.target as HTMLTextAreaElement).value)}
        ></sl-textarea>
        <sl-button slot="footer" @click=${() => (this._endDialogOpen = false)}
          >Cancel</sl-button
        >
        <sl-button
          slot="footer"
          variant="warning"
          ?loading=${this._busy}
          ?disabled=${this._busy}
          @click=${this._submitEnd}
          >End shift</sl-button
        >
      </sl-dialog>

      <sl-dialog
        label="Hand over the till"
        ?open=${this._handoverDialogOpen}
        @sl-request-close=${() => (this._handoverDialogOpen = false)}
      >
        <p class="notice">
          The incoming cashier enters their staff PIN to take over. The drawer
          is NOT recounted — its expected balance carries to the new shift.
          (Staff PINs are a Silver-tier feature.)
        </p>
        <sl-input
          label="Incoming cashier PIN"
          type="password"
          inputmode="numeric"
          autocomplete="off"
          minlength="4"
          maxlength="8"
          .value=${this._handoverPin}
          @sl-input=${(e: Event) => (this._handoverPin = (e.target as HTMLInputElement).value)}
          autofocus
          required
        ></sl-input>
        <sl-button
          slot="footer"
          @click=${() => (this._handoverDialogOpen = false)}
          >Cancel</sl-button
        >
        <sl-button
          slot="footer"
          variant="primary"
          ?loading=${this._busy}
          ?disabled=${this._busy}
          @click=${this._submitHandover}
          >Hand over</sl-button
        >
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'shop-day-bar': ShopDayBar;
  }
}
